import { boardblendApi } from "@/store/api";
import type { JoinResult } from "@/types/session";
import type { WorkshopSnapshot } from "@/types/workshop";
import type { WorkshopAction } from "@/server/workshop-values";

export const workshopApi = boardblendApi.injectEndpoints({
  endpoints: (build) => ({
    createWorkshop: build.mutation<JoinResult, { displayName?: string }>({
      query: (body) => ({ url: "workshops", method: "POST", body }),
      invalidatesTags: ["AccountSessions"],
    }),
    getWorkshop: build.query<WorkshopSnapshot, string>({
      query: (id) => `sessions/${id}/workshop`,
      providesTags: (_result, _error, id) => [{ type: "Workshop", id }],
      merge: (current, incoming) =>
        incoming.revision > current.revision ? incoming : current,
    }),
    updateWorkshop: build.mutation<
      WorkshopSnapshot,
      { sessionId: string; body: WorkshopAction }
    >({
      query: ({ sessionId, body }) => ({
        url: `sessions/${sessionId}/workshop`,
        method: "PATCH",
        body,
      }),
      async onQueryStarted(
        { sessionId, body },
        { dispatch, getState, queryFulfilled },
      ) {
        const select = () =>
          workshopApi.endpoints.getWorkshop.select(sessionId)(getState()).data;
        const previous = select();
        const patch = dispatch(
          workshopApi.util.updateQueryData(
            "getWorkshop",
            sessionId,
            (draft) => {
              if (
                body.action === "start" &&
                draft.phase === "lobby" &&
                draft.questions.length
              ) {
                draft.activeQuestionId = draft.questions[0].id;
                draft.phase = "thinking";
              }
              if (
                body.action !== "next" ||
                draft.phase !== "discussion" ||
                draft.activeQuestionId !== body.questionId
              )
                return;
              const index = draft.questions.findIndex(
                (question) => question.id === body.questionId,
              );
              const next = draft.questions[index + 1];
              draft.selectedContributionId = null;
              if (!next) {
                draft.phase = "finished";
                return;
              }
              draft.activeQuestionId = next.id;
              draft.phase = "thinking";
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          if (data.revision < (select()?.revision ?? 0)) return;
          dispatch(
            workshopApi.util.updateQueryData(
              "getWorkshop",
              sessionId,
              () => data,
            ),
          );
        } catch {
          if (select()?.revision === previous?.revision) patch.undo();
          // The mutation hook exposes the error to the room.
        }
      },
    }),
  }),
});
export const {
  useCreateWorkshopMutation,
  useGetWorkshopQuery,
  useUpdateWorkshopMutation,
} = workshopApi;
